import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const nice = (d) => {
  if (!d) return "";
  const [y, m, day] = String(d).slice(0, 10).split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(day)} ${months[Number(m) - 1]} ${y}`;
};

const monthName = (m) => {
  if (!m) return "";
  const [y, mm] = String(m).split("-");
  return new Date(Number(y), Number(mm) - 1, 1)
    .toLocaleString("en-IN", { month: "long", year: "numeric" });
};

export default function StaffPayHistory({ staffId, onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [from, setFrom] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/staffing/${staffId}/timeline`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    if (staffId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId]);

  // Show what the change would do before anyone commits to it. Asking the
  // server rather than repeating the split maths here keeps one answer.
  useEffect(() => {
    const amt = Number(amount);
    if (!open || !amt || !from || from.length < 10) { setPreview(null); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(
          `/staffing/${staffId}/pay-revisions/preview`,
          { amount: amt, effective_from: from });
        if (alive) setPreview(data);
      } catch (e) {
        if (alive) setPreview({ error: formatApiError(e) });
      }
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [open, amount, from, staffId]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staffing/${staffId}/pay-revisions`, {
        amount: Number(amount), effective_from: from, note });
      const n = (data.restated || []).length;
      toast.success(n ? `Saved. ${n} payslip${n === 1 ? "" : "s"} re-generated.`
                      : "Pay revision saved.");
      (data.left_alone || []).forEach((x) =>
        toast.message(`${monthName(x.month)} left as it is`, {
          description: "That month was set by hand, so it was not overwritten." }));
      setOpen(false); setAmount(""); setFrom(""); setNote(""); setPreview(null);
      load();
      if (onChanged) onChanged(data.contractor);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const drop = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/staffing/${staffId}/pay-revisions/${id}`);
      const n = (data.restated || []).length;
      toast.success(n ? `Removed. ${n} payslip${n === 1 ? "" : "s"} re-generated.`
                      : "Revision removed.");
      load();
      if (onChanged) onChanged(data.contractor);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const events = data.events || [];

  return (
    <div className="rounded-lg border border-zinc-200">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-zinc-100">
        <div>
          <div className="text-sm font-semibold">Pay &amp; history</div>
          <div className="text-[11px] text-zinc-500">
            Currently {inr(data.current)} a month
          </div>
        </div>
        <Button size="sm" className="h-7 text-xs" data-testid="revise-pay"
                onClick={() => setOpen(true)}>
          Revise pay
        </Button>
      </div>

      <div className="divide-y divide-zinc-100">
        {events.map((e, i) => (
          <div key={`${e.kind}-${e.date}-${i}`}
               className="flex items-start justify-between gap-3 px-3 py-2.5">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{e.title}</span>
                <Badge variant="outline" className="text-[10px]">{nice(e.date)}</Badge>
              </div>
              {e.kind === "pay_revision" && e.from_amount != null && (
                <div className="text-xs text-zinc-600 mt-0.5">
                  {inr(e.from_amount)} &rarr; {inr(e.amount)}
                </div>
              )}
              {e.kind !== "pay_revision" && e.amount != null && (
                <div className="text-xs text-zinc-600 mt-0.5">{inr(e.amount)} a month</div>
              )}
              {e.detail && (
                <div className="text-[11px] text-zinc-500 mt-0.5">{e.detail}</div>
              )}
              {e.recorded_by && (
                <div className="text-[11px] text-zinc-400 mt-0.5">
                  Recorded by {e.recorded_by}
                </div>
              )}
            </div>
            {e.kind === "pay_revision" && e.id && (
              <button type="button" disabled={busy} onClick={() => drop(e.id)}
                      className="text-[11px] text-zinc-400 hover:text-red-600 shrink-0">
                Remove
              </button>
            )}
          </div>
        ))}
        {!events.length && (
          <div className="px-3 py-4 text-xs text-zinc-500">Nothing recorded yet.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md space-y-3" data-testid="revise-pay-dialog">
          <DialogHeader><DialogTitle>Revise pay</DialogTitle></DialogHeader>

          <div className="space-y-1">
            <label className="text-xs text-zinc-600">New monthly amount</label>
            <Input type="number" value={amount} placeholder="22000"
                   onChange={(ev) => setAmount(ev.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-600">Effective from</label>
            <Input type="date" value={from}
                   onChange={(ev) => setFrom(ev.target.value)} />
            <div className="text-[11px] text-zinc-500">
              Pay before this date is left exactly as it is.
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-600">Reason (optional)</label>
            <Input value={note} placeholder="Annual hike"
                   onChange={(ev) => setNote(ev.target.value)} />
          </div>

          {preview && preview.error && (
            <div className="text-xs text-red-600">{preview.error}</div>
          )}

          {preview && !preview.error && (
            <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 space-y-1">
              <div className="text-[11px] uppercase tracking-wide text-zinc-500">
                {monthName(preview.month)}
              </div>
              {(preview.segments || []).map((s, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-zinc-600">
                    {nice(s.from)} &ndash; {nice(s.to)} &middot; {s.days}d @ {inr(s.rate)}
                  </span>
                  <span>{inr(s.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs font-medium pt-1 border-t border-zinc-200">
                <span>Month total</span><span>{inr(preview.month_total)}</span>
              </div>
              {preview.was_month_total !== preview.month_total && (
                <div className="text-[11px] text-zinc-500">
                  Was {inr(preview.was_month_total)} for this month.
                </div>
              )}
              {(preview.affects || []).length > 0 && (
                <div className="text-[11px] text-amber-700 pt-1">
                  {preview.affects.filter((a) => !a.by_hand).length > 0 && (
                    <div>
                      Will re-generate:{" "}
                      {preview.affects.filter((a) => !a.by_hand)
                        .map((a) => monthName(a.month)).join(", ")}
                    </div>
                  )}
                  {preview.affects.filter((a) => a.by_hand).length > 0 && (
                    <div>
                      Left as set by hand:{" "}
                      {preview.affects.filter((a) => a.by_hand)
                        .map((a) => monthName(a.month)).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" data-testid="revise-pay-save"
                    disabled={busy || !Number(amount) || !from}
                    onClick={save}>
              Save revision
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
